import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MegaphoneSimple, Plus, Play, Pause, GearSix, ArrowsClockwise, PencilSimple, BellRinging } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { AdsCampaignWizard } from "./AdsCampaignWizard";

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm placeholder:text-white/25";
const fmtCid = (cid) => (cid || "").replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");

const STATUS_STYLE = {
  ENABLED: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  PAUSED: "bg-amber-400/15 text-amber-300 border-amber-400/30",
  REMOVED: "bg-red-500/15 text-red-400 border-red-500/30",
};
const STATUS_LABEL = { ENABLED: "Attiva", PAUSED: "In pausa", REMOVED: "Rimossa" };

const AdsVsSeoChart = ({ data }) => {
  const W = 600, H = 140, P = 4;
  const lines = [
    { key: "seo", label: `Clic SEO (${data.totals.seo_clicks})`, color: "#34D399", vals: data.seo_clicks },
    { key: "adsclicks", label: `Clic Ads (${data.totals.ads_clicks})`, color: "#38BDF8", vals: data.ads_clicks },
    { key: "spend", label: `Spesa Ads (€ ${data.totals.spend.toFixed(2)})`, color: "#FBBF24", vals: data.ads_spend },
  ];
  const toPts = (vals) => {
    const max = Math.max(...vals, 1);
    return vals.map((v, i) => `${P + (i / Math.max(vals.length - 1, 1)) * (W - 2 * P)},${H - P - (v / max) * (H - 2 * P)}`).join(" ");
  };
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36" preserveAspectRatio="none">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={P} x2={W - P} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}
        {lines.map((l) => (
          <polyline key={l.key} points={toPts(l.vals)} fill="none" stroke={l.color}
            strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.9"
            strokeDasharray={l.key === "spend" ? "5,3" : "none"} />
        ))}
      </svg>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
        {lines.map((l) => (
          <span key={l.key} className="inline-flex items-center gap-1.5 text-xs text-white/60">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} /> {l.label}
          </span>
        ))}
      </div>
    </>
  );
};

export const GoogleAdsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [budgetEdit, setBudgetEdit] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [vsSeo, setVsSeo] = useState(null);
  const [alertCfg, setAlertCfg] = useState({ threshold: "", enabled: false, monthly_threshold: "", monthly_enabled: false, perf_enabled: false });
  const [savingAlert, setSavingAlert] = useState(false);

  const loadCfg = () => api.get("/admin/ads/config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { loadCfg(); }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/ads/campaigns");
      setCampaigns(data.campaigns);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setCampaigns(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cfg?.connected) {
      api.get("/admin/ads/accounts").then(({ data }) => setAccounts(data.accounts)).catch(() => setAccounts([]));
    }
  }, [cfg?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cfg?.configured) {
      loadCampaigns();
      api.get("/admin/ads/vs-seo?days=28").then(({ data }) => setVsSeo(data)).catch(() => setVsSeo(null));
      api.get("/admin/ads/alert").then(({ data }) => setAlertCfg({
        threshold: data.threshold ?? "", enabled: data.enabled,
        monthly_threshold: data.monthly_threshold ?? "", monthly_enabled: data.monthly_enabled,
        perf_enabled: data.perf_enabled || false,
      })).catch(() => {});
    }
  }, [cfg?.configured, cfg?.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAccount = async (cid) => {
    if (cid === cfg?.customer_id) return;
    setSwitching(cid);
    try {
      const { data } = await api.put("/admin/ads/account", { customer_id: cid });
      toast.success(data.message);
      await loadCfg();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSwitching(null);
    }
  };

  const handleSaveAlert = async () => {
    setSavingAlert(true);
    try {
      const { data } = await api.put("/admin/ads/alert", {
        threshold: alertCfg.threshold ? parseFloat(alertCfg.threshold) : null,
        enabled: alertCfg.enabled,
        monthly_threshold: alertCfg.monthly_threshold ? parseFloat(alertCfg.monthly_threshold) : null,
        monthly_enabled: alertCfg.monthly_enabled,
        perf_enabled: alertCfg.perf_enabled,
      });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingAlert(false);
    }
  };

  const toggleStatus = async (c) => {
    setTogglingId(c.id);
    try {
      const { data } = await api.put(`/admin/ads/campaigns/${c.id}/status`, { status: c.status === "PAUSED" ? "ENABLED" : "PAUSED" });
      toast.success(data.message);
      loadCampaigns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    setSavingBudget(true);
    try {
      const { data } = await api.put(`/admin/ads/campaigns/${budgetEdit.id}/budget`, { daily_budget: parseFloat(budgetEdit.value) });
      toast.success(data.message);
      setBudgetEdit(null);
      loadCampaigns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBudget(false);
    }
  };

  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border mb-8 animate-fade-up" data-testid="google-ads-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <MegaphoneSimple size={32} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Google Ads</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {cfg?.configured
                ? <>Account collegato: <span className="text-white/80">{fmtCid(cfg.customer_id)}</span> — spesa, clic e impressioni degli ultimi 30 giorni.</>
                : cfg?.connected
                  ? "Account Google collegato: seleziona qui sotto l'account Google Ads da gestire."
                  : "Collega il tuo account Google Ads per creare e gestire le campagne da qui."}
            </p>
          </div>
        </div>
        {cfg?.configured && (
          <div className="flex gap-2">
            <Button data-testid="ads-reload-button" onClick={loadCampaigns} disabled={loading} variant="ghost"
              className="h-9 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
              <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Ricarica
            </Button>
            <Button data-testid="ads-new-campaign-button" onClick={() => setCreateOpen(true)}
              className="h-9 px-4 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold">
              <Plus size={14} className="mr-1" /> Nuova campagna
            </Button>
            <Link to="/impostazioni" data-testid="ads-edit-creds-button" title="Credenziali e connessione (Impostazioni)"
              className="h-9 w-9 border border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-full inline-flex items-center justify-center transition-colors">
              <GearSix size={15} />
            </Link>
          </div>
        )}
      </div>

      {cfg && !cfg.connected && (
        <div className="mt-5 p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="ads-not-connected">
          <p className="text-sm text-white/60">
            {cfg.has_credentials
              ? 'Credenziali salvate: completa il collegamento con "Accedi con Google" dalle Impostazioni.'
              : "Nessun account collegato. Configura le credenziali (Client ID, Secret, Developer Token) e collega Google con OAuth: trovi anche la guida passo-passo."}
          </p>
          <Link to="/impostazioni" data-testid="ads-goto-settings"
            className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-blue-300 hover:text-blue-200 transition-colors">
            Vai a Impostazioni → Google_Ads →
          </Link>
        </div>
      )}

      {cfg?.connected && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="ads-account-selector">
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/35 mr-1">Account</span>
          {accounts === null && <span className="text-xs text-white/40">Caricamento account...</span>}
          {accounts && accounts.length === 0 && <span className="text-xs text-amber-400">Nessun account Google Ads accessibile con l'utente collegato.</span>}
          {(accounts || []).map((a) => {
            const active = cfg.customer_id === a.customer_id;
            return (
              <button key={a.customer_id} type="button" disabled={switching === a.customer_id}
                data-testid={`ads-account-${a.customer_id}`} onClick={() => selectAccount(a.customer_id)}
                className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${active ? "bg-blue-500/15 text-blue-300 border-blue-400/50" : "border-white/15 text-white/55 hover:text-white hover:bg-white/5"}`}>
                {switching === a.customer_id ? "..." : (a.name || fmtCid(a.customer_id))}
                {a.manager && <span className="text-[9px] uppercase tracking-wider opacity-60">MCC</span>}
                {a.name && <span className="text-[10px] opacity-50">{fmtCid(a.customer_id)}</span>}
              </button>
            );
          })}
        </div>
      )}

      {cfg?.configured && (
        <div className="mt-5">
          {error && (
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-xs" data-testid="ads-error">
              {error}
            </div>
          )}
          {loading && !campaigns && <p className="text-xs text-white/40 py-4 text-center">Caricamento campagne...</p>}
          {campaigns && campaigns.length === 0 && (
            <p className="text-xs text-white/40 py-4 text-center" data-testid="ads-empty">Nessuna campagna nell'account. Creane una con "Nuova campagna".</p>
          )}
          {campaigns && campaigns.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ads-campaigns-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
                    <th className="py-2 pr-2 font-medium">Campagna</th>
                    <th className="py-2 pr-2 font-medium">Stato</th>
                    <th className="py-2 pr-2 font-medium">Tipo</th>
                    <th className="py-2 pr-2 font-medium text-right">Budget/giorno</th>
                    <th className="py-2 pr-2 font-medium text-right">Spesa 30gg</th>
                    <th className="py-2 pr-2 font-medium text-right">Clic</th>
                    <th className="py-2 pr-2 font-medium text-right">Impressioni</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-t border-white/5 text-white/75" data-testid={`ads-campaign-${c.id}`}>
                      <td className="py-2.5 pr-2 font-semibold truncate max-w-[200px]" title={c.name}>{c.name}</td>
                      <td className="py-2.5 pr-2">
                        <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLE[c.status] || "bg-white/5 text-white/50 border-white/15"}`}>
                          {STATUS_LABEL[c.status] || c.status}
                        </span>
                      </td>
                      <td className="py-2.5 pr-2 text-white/45">{c.channel === "SEARCH" ? "Ricerca" : c.channel}</td>
                      <td className="py-2.5 pr-2 text-right">
                        <button type="button" data-testid={`ads-budget-${c.id}`} title="Modifica budget giornaliero"
                          onClick={() => setBudgetEdit({ id: c.id, name: c.name, value: String(c.budget ?? "") })}
                          className="inline-flex items-center gap-1 text-white/75 hover:text-white transition-colors">
                          € {(c.budget ?? 0).toFixed(2)} <PencilSimple size={11} className="opacity-50" />
                        </button>
                      </td>
                      <td className="py-2.5 pr-2 text-right font-semibold">€ {c.spend.toFixed(2)}</td>
                      <td className="py-2.5 pr-2 text-right">{c.clicks}</td>
                      <td className="py-2.5 pr-2 text-right">{c.impressions}</td>
                      <td className="py-2.5 text-right">
                        {c.status !== "REMOVED" && (
                          <Button size="sm" variant="ghost" disabled={togglingId === c.id}
                            data-testid={`ads-toggle-${c.id}`} onClick={() => toggleStatus(c)}
                            className={`h-7 px-3 rounded-full text-[11px] font-semibold border ${c.status === "PAUSED" ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" : "border-amber-400/40 text-amber-300 hover:bg-amber-400/10"}`}>
                            {c.status === "PAUSED" ? <><Play size={11} className="mr-1" /> Attiva</> : <><Pause size={11} className="mr-1" /> Pausa</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cfg?.configured && (
            <div className="mt-6 pt-5 border-t border-white/5" data-testid="ads-alert-row">
              <div className="flex items-center gap-2 mb-3">
                <BellRinging size={18} weight="duotone" className={alertCfg.enabled || alertCfg.monthly_enabled ? "text-amber-400" : "text-white/30"} />
                <div>
                  <p className="text-xs font-semibold text-white/80">Avvisi di spesa e prestazioni</p>
                  <p className="text-[10px] text-white/40">Email + notifica push ai Master ogni mattina: spesa di ieri, tetto mensile (80% e superamento), calo prestazioni (clic -40% vs 7gg precedenti) e budget campagne in esaurimento. Il controllo prestazioni vale anche per le campagne Meta e TikTok collegate.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white/50 w-28">Soglia giornaliera</span>
                  <Input type="number" min="0.01" step="0.01" data-testid="ads-alert-threshold-input"
                    value={alertCfg.threshold} onChange={(e) => setAlertCfg({ ...alertCfg, threshold: e.target.value })}
                    placeholder="€ / giorno" className="h-9 w-28 bg-transparent border-white/10 text-white text-sm" />
                  <button type="button" data-testid="ads-alert-toggle" onClick={() => setAlertCfg({ ...alertCfg, enabled: !alertCfg.enabled })}
                    className={`h-9 px-4 rounded-full text-[11px] font-semibold border transition-colors ${alertCfg.enabled ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "border-white/15 text-white/50 hover:text-white"}`}>
                    {alertCfg.enabled ? "Attivo" : "Disattivo"}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white/50 w-28">Tetto mensile</span>
                  <Input type="number" min="0.01" step="0.01" data-testid="ads-alert-monthly-input"
                    value={alertCfg.monthly_threshold} onChange={(e) => setAlertCfg({ ...alertCfg, monthly_threshold: e.target.value })}
                    placeholder="€ / mese" className="h-9 w-28 bg-transparent border-white/10 text-white text-sm" />
                  <button type="button" data-testid="ads-alert-monthly-toggle" onClick={() => setAlertCfg({ ...alertCfg, monthly_enabled: !alertCfg.monthly_enabled })}
                    className={`h-9 px-4 rounded-full text-[11px] font-semibold border transition-colors ${alertCfg.monthly_enabled ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "border-white/15 text-white/50 hover:text-white"}`}>
                    {alertCfg.monthly_enabled ? "Attivo" : "Disattivo"}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white/50">Prestazioni e budget campagne</span>
                  <button type="button" data-testid="ads-alert-perf-toggle" onClick={() => setAlertCfg({ ...alertCfg, perf_enabled: !alertCfg.perf_enabled })}
                    className={`h-9 px-4 rounded-full text-[11px] font-semibold border transition-colors ${alertCfg.perf_enabled ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "border-white/15 text-white/50 hover:text-white"}`}>
                    {alertCfg.perf_enabled ? "Attivo" : "Disattivo"}
                  </button>
                </div>
                <Button data-testid="ads-alert-save-button" onClick={handleSaveAlert} disabled={savingAlert}
                  className="h-9 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold ml-auto">
                  {savingAlert ? "..." : "Salva avvisi"}
                </Button>
              </div>
            </div>
          )}
          {vsSeo && (
            <div className="mt-6 pt-5 border-t border-white/5" data-testid="ads-vs-seo-chart">
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Organico vs A pagamento (ultimi 28 giorni)</p>
              <AdsVsSeoChart data={vsSeo} />
              <p className="text-[10px] text-white/30 mt-2">Ogni linea è in scala propria per confrontare gli andamenti; i totali assoluti sono nella legenda. Linea tratteggiata = spesa giornaliera Google Ads.</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!budgetEdit} onOpenChange={(o) => !o && setBudgetEdit(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-sm" data-testid="ads-budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">Modifica budget giornaliero</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Campagna: <span className="text-white/80">{budgetEdit?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveBudget} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nuovo budget (€/giorno)</Label>
              <Input required autoFocus type="number" min="0.01" step="0.01" data-testid="ads-budget-input"
                value={budgetEdit?.value || ""} onChange={(e) => setBudgetEdit({ ...budgetEdit, value: e.target.value })}
                className="h-11 bg-transparent border-white/10 text-white" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={savingBudget} data-testid="ads-budget-save-button"
                className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                {savingBudget ? "Salvataggio..." : "Salva budget"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AdsCampaignWizard open={createOpen} onClose={() => setCreateOpen(false)} onCreated={loadCampaigns} />
    </div>
  );
};
